import React, { useState, useEffect, useMemo } from "react";

import {
  Users,
  Database,
  TrendingUp,
  Upload,
  Search,
  Filter,
  ChevronRight,
  CheckCircle,
  Check,
  X,
  Eye,
  Clock,
  Bell,
  Inbox,
  Layers,
  Grid3x3,
  UserRound,
} from "lucide-react";
import { useLocation } from "react-router-dom";

import ApprovalModal from "../../Components/ApprovalModal";
import Button from "../../Components/Button";
import DataUploadManager from "../../Components/DataUploadManager";
import DropdownSelector from "../../Components/DropdownSelector";
import CommonModal from "../../Components/Modal";
import PreviewModal from "../../Components/PreviewModal";
import RejectionModal from "../../Components/RejectionModal";
import SuccessNotification from "../../Components/SuccessNotification";
import TabsComponent from "../../Components/Tab";
import DataHubPage from "../../data-hub-library/DataHubPage";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchApprovalRequests,
  approveOrRejectRequest,
  ApprovalRequestItem,
  clearApproveRejectError,
  clearApproveRejectSuccess,
} from "../../Store/Reducers/OpsSlice";
import { formatDateUSShort, canApproveReject } from "../../Utils/Helper/Helper";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

interface UploadItem {
  id: string;
  name: string;
  type: "executives" | "composite" | "team";
  uploadType?: any;
  records: number;
  uploader: string;
  status: "pending" | "rejected" | "approved" | "activated";
  date: string;
  expanded?: boolean;
  uploadDate?: string;
  approvedBy?: string;
  approvalDate?: string;
  rejectedBy?: string;
  rejectionDate?: string;
  rejectionReason?: string;
  activatedBy?: string;
  activationDate?: string;
  previewData?: any[]; // Raw API data - customers or users
  approverId?: string | null;
  approverName?: string | null;
}

/** Maps Ex Ops sub-tab id to API upload type and Upload modal default type. */
const EX_OPS_TAB_UPLOAD_MAP: Record<
  string,
  { apiType?: string; modalType: "executives" | "accounts" | "composite" }
> = {
  all: { modalType: "executives" },
  sponsors: { apiType: "user", modalType: "executives" },
  accounts: { apiType: "customer", modalType: "accounts" },
  composites: { apiType: "composite", modalType: "composite" },
};

/** Maps Team Upload sub-tab id to API upload type and Upload modal default type. */
const TEAM_TAB_UPLOAD_MAP: Record<
  string,
  {
    apiType?: string;
    modalType: "upload_account_team_members" | "upload_customer_team_members";
  }
> = {
  all: { modalType: "upload_account_team_members" },
  "account-team": {
    apiType: "upload_account_team_members",
    modalType: "upload_account_team_members",
  },
  "customer-team": {
    apiType: "upload_customer_team_members",
    modalType: "upload_customer_team_members",
  },
};

const TEAM_UPLOAD_TYPES = new Set([
  "upload_account_team_members",
  "upload_customer_team_members",
  "add_account_team_members",
  "add_customer_team_members",
  "update_account_team_members",
  "update_customer_team_members",
]);

const isTeamUploadType = (uploadType?: string): boolean =>
  !!uploadType && TEAM_UPLOAD_TYPES.has(uploadType);

const OverviewContainer: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const canApprove = canApproveReject(loggedInUser?.role);
  const loggedInUserId = loggedInUser?.userId
    ? String(loggedInUser.userId)
    : "";

  /** True if the current user can approve/reject this item (role allows it and approver_id matches or is not set). */
  const canApproveForItem = (item: UploadItem): boolean => {
    return true; // as of now we are allowing all users to approve/reject irrespective of role or approver_id, but this can be enabled in future when needed
    if (!canApprove) return false;
    const approverId = item.approverId?.trim();
    if (!approverId) return true;
    return approverId === loggedInUserId;
  };
  const { data: approvalRequestsData, loading: approvalRequestsLoading } =
    useAppSelector(state => state.ops.approvalRequests);
  const { error: approveRejectError, success: approveRejectSuccess } =
    useAppSelector(state => state.ops.approveReject);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [itemsPerPage, setItemsPerPage] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedPreviewItem, setSelectedPreviewItem] =
    useState<UploadItem | null>(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedApprovalItem, setSelectedApprovalItem] =
    useState<UploadItem | null>(null);
  const [isSuccessNotificationOpen, setIsSuccessNotificationOpen] =
    useState(false);
  const [successMessage, setSuccessMessage] = useState({
    message: "",
    details: "",
  });
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [selectedRejectionItem, setSelectedRejectionItem] =
    useState<UploadItem | null>(null);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [activeUploadCard, setActiveUploadCard] = useState<string>(() => {
    const params = new URLSearchParams(location.search);
    // Deep links (Always On → Configure KPIs) should land on Metrics Upload
    // on the first paint, before the search-param effect runs.
    return params.get("metricsTab") ? "metrics-data-upload" : "ex-ops-upload";
  });
  const [metricsHeaderActionsEl, setMetricsHeaderActionsEl] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Deep links from Hawkeye / KPI banners land on Metrics Upload.
    // The old v1 pipeline is replaced by the same Data Hub console as /data-hub.
    if (params.get("metricsTab")) {
      setActiveUploadCard("metrics-data-upload");
    }
  }, [location.search]);

  const getActiveApiType = (): string | undefined => {
    if (activeUploadCard === "ex-ops-upload") {
      return EX_OPS_TAB_UPLOAD_MAP[activeTab]?.apiType;
    }
    if (activeUploadCard === "teams-upload") {
      return TEAM_TAB_UPLOAD_MAP[activeTab]?.apiType;
    }
    return undefined;
  };

  // Fetch approval requests based on active upload card and sub-tab
  useEffect(() => {
    if (
      activeUploadCard !== "ex-ops-upload" &&
      activeUploadCard !== "teams-upload"
    ) {
      return;
    }
    dispatch(fetchApprovalRequests(getActiveApiType()));
    setCurrentPage(1);
  }, [dispatch, activeTab, activeUploadCard]);

  // Handle approve/reject success
  useEffect(() => {
    if (approveRejectSuccess) {
      if (
        activeUploadCard === "ex-ops-upload" ||
        activeUploadCard === "teams-upload"
      ) {
        dispatch(fetchApprovalRequests(getActiveApiType()));
      }
      dispatch(clearApproveRejectSuccess());
    }
  }, [approveRejectSuccess, dispatch, activeTab, activeUploadCard]);

  // Handle approve/reject errors
  useEffect(() => {
    if (approveRejectError) {
      // You can show an error notification here if needed
      console.error("Approve/Reject error:", approveRejectError);
      dispatch(clearApproveRejectError());
    }
  }, [approveRejectError, dispatch]);

  // Map API response to UploadItem format
  const mapApprovalRequestToUploadItem = (
    item: ApprovalRequestItem
  ): UploadItem => {
    const type: UploadItem["type"] = isTeamUploadType(item.upload_type)
      ? "team"
      : item.upload_type === "users"
        ? "executives"
        : "composite";

    // Pass raw API data based on upload type
    let previewData: any[] = [];

    if (isTeamUploadType(item.upload_type)) {
      if (item.users && item.users.length > 0) {
        previewData = item.users;
      }
    } else if (item.upload_type === "composite") {
      // For composite type, use the composite array directly
      if (item.composite && item.composite.length > 0) {
        previewData = item.composite;
      }
    } else {
      // For customers or users type, combine them
      if (item.customers && item.customers.length > 0) {
        previewData.push(...item.customers);
      }

      if (item.users && item.users.length > 0) {
        previewData.push(...item.users);
      }
    }

    // Map updated_by and updated_at based on status
    let approvedBy: string | undefined;
    let approvalDate: string | undefined;
    let rejectedBy: string | undefined;
    let rejectionDate: string | undefined;
    let activatedBy: string | undefined;
    let activationDate: string | undefined;

    if (item.updated_by && item.updated_at) {
      if (item.status === "approved") {
        approvedBy = item.updated_by;
        approvalDate = formatDateUSShort(item.updated_at);
      } else if (item.status === "rejected") {
        rejectedBy = item.updated_by;
        rejectionDate = formatDateUSShort(item.updated_at);
      } else if (item.status === "activated") {
        activatedBy = item.updated_by;
        activationDate = formatDateUSShort(item.updated_at);
        // For activated items, updated_by/updated_at represents activation
        // Approval info might be in a separate field or same person
        // If approval info is needed separately, it should come from API
      }
    }

    return {
      id: item.batch_id,
      uploadType: item.upload_type,
      name: item.batch_name,
      type: type,
      records: item.record_count,
      uploader: item.uploaded_by_name,
      status: item.status,
      date: formatDateUSShort(item.uploaded_at),
      uploadDate: formatDateUSShort(item.uploaded_at),
      previewData: previewData.length > 0 ? previewData : undefined,
      approvedBy: approvedBy,
      approvalDate: approvalDate,
      rejectedBy: rejectedBy,
      rejectionDate: rejectionDate,
      activatedBy: activatedBy,
      activationDate: activationDate,
      approverId: item.approver_id ?? undefined,
      approverName: item.approver_name ?? undefined,
    };
  };

  // Transform API data to UploadItem array
  const uploadData = useMemo(() => {
    if (!approvalRequestsData?.items) return [];
    const items = approvalRequestsData.items.map(
      mapApprovalRequestToUploadItem
    );

    if (activeUploadCard === "teams-upload" && activeTab === "all") {
      return items.filter(item => isTeamUploadType(item.uploadType));
    }

    if (activeUploadCard === "ex-ops-upload") {
      return items.filter(item => !isTeamUploadType(item.uploadType));
    }

    return items;
  }, [approvalRequestsData, activeUploadCard, activeTab]);

  const metricsOrgName =
    loggedInUser?.organizationName || loggedInUser?.organization_name || "";

  const uploadTypeViews = [
    {
      id: "ex-ops-upload",
      label: "Executive Ops Upload",
      icon: <Upload size={14} />,
    },
    {
      id: "teams-upload",
      label: "Team Upload",
      icon: <Users size={14} />,
    },
    {
      id: "metrics-data-upload",
      label: "Metrics Upload",
      icon: <TrendingUp size={14} />,
    },
  ];

  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case "pending":
        return "border-l-amber-500 hover:border-l-amber-600";
      case "rejected":
        return "border-l-red-500 hover:border-l-red-600";
      case "approved":
        return "border-l-blue-500 hover:border-l-blue-600";
      case "activated":
        return "border-l-green-500 hover:border-l-green-600";
      default:
        return "";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-amber-500 text-white";
      case "rejected":
        return "bg-red-500 text-white";
      case "approved":
        return "bg-blue-500 text-white";
      case "activated":
        return "bg-green-500 text-white";
      default:
        return "";
    }
  };

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case "pending":
        return "text-amber-500";
      case "rejected":
        return "text-red-600";
      case "approved":
        return "text-blue-600";
      case "activated":
        return "text-green-600";
      default:
        return "";
    }
  };

  // Check if upload type is a bulk upload type (users, customers, or composite)
  const isBulkUploadType = (uploadType: string | undefined): boolean => {
    return (
      uploadType === "users" ||
      uploadType === "customers" ||
      uploadType === "composite"
    );
  };

  // Format upload name based on upload type
  const formatUploadName = (name: string, uploadType: string): string => {
    if (!name || !uploadType) return name;
    let formattedName = name;

    // Handle add operations - replace "Add" with "Added"
    if (uploadType === "add_customer" || uploadType === "add_user") {
      // Replace "Bulk Add" with "Bulk Added"
      formattedName = formattedName.replace(/\bBulk Add\b/gi, "Bulk Added");
      // Replace standalone "Add" (not part of "Added") with "Added"
      formattedName = formattedName.replace(/\bAdd\b(?!ded)/gi, "Added");
      // Replace "Executives Add" with "Executives Added"
      formattedName = formattedName.replace(
        /\bExecutives Add\b/gi,
        "Executives Added"
      );
      // Replace "Accounts Add" with "Accounts Added"
      formattedName = formattedName.replace(
        /\bAccounts Add\b/gi,
        "Accounts Added"
      );
    }

    // Handle update operations - replace "Update" with "Updated"
    if (uploadType === "customer_update" || uploadType === "user_update") {
      // Replace "Bulk Update" with "Bulk Updated"
      formattedName = formattedName.replace(
        /\bBulk Update\b/gi,
        "Bulk Updated"
      );
      // Replace standalone "Update" (not part of "Updated") with "Updated"
      formattedName = formattedName.replace(/\bUpdate\b(?!d)/gi, "Updated");
      // Replace "Executives Update" with "Executives Updated"
      formattedName = formattedName.replace(
        /\bExecutives Update\b/gi,
        "Executives Updated"
      );
      // Replace "Accounts Update" with "Accounts Updated"
      formattedName = formattedName.replace(
        /\bAccounts Update\b/gi,
        "Accounts Updated"
      );
    }

    // Handle delete operations
    if (uploadType === "delete_customer") {
      // Replace "Delete" with "Deleted" if not already "Deleted"
      formattedName = formattedName.replace(/\bDelete\b(?!d)/gi, "Deleted");
      // Replace "Bulk Delete" with "Bulk Deleted"
      formattedName = formattedName.replace(
        /\bBulk Delete\b/gi,
        "Bulk Deleted"
      );
      // Replace "Account Delete" with "Account Deleted"
      formattedName = formattedName.replace(
        /\bAccount Delete\b/gi,
        "Account Deleted"
      );
    }

    // Handle bulk upload types (users, customers, composite)
    if (isBulkUploadType(uploadType)) {
      // If name contains "Add" but not "Added", replace with "Added"
      if (formattedName.match(/\bAdd\b(?!ded)/i)) {
        formattedName = formattedName.replace(/\bBulk Add\b/gi, "Bulk Added");
        formattedName = formattedName.replace(/\bAdd\b(?!ded)/gi, "Added");
      }
      // If name contains "Upload" but not "Uploaded", replace with "Uploaded"
      if (formattedName.match(/\bUpload\b(?!ed)/i)) {
        formattedName = formattedName.replace(
          /\bBulk Upload\b/gi,
          "Bulk Uploaded"
        );
        formattedName = formattedName.replace(/\bUpload\b(?!ed)/gi, "Uploaded");
      }
    }

    return formattedName;
  };

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const handlePreviewClick = (item: UploadItem) => {
    setSelectedPreviewItem(item);
    setIsPreviewModalOpen(true);
  };

  const handleApproveClick = (item: UploadItem) => {
    setSelectedApprovalItem(item);
    setIsApprovalModalOpen(true);
  };

  const handleApprovalConfirm = async () => {
    if (!selectedApprovalItem) return;

    try {
      // Dispatch API call to approve the request
      const result = await dispatch(
        approveOrRejectRequest({
          batch_id: selectedApprovalItem.id,
          status: "approved",
        })
      );

      // Check if the action was fulfilled
      if (approveOrRejectRequest.fulfilled.match(result)) {
        // Close the approval modal
        setIsApprovalModalOpen(false);

        // Show success notification
        const approvalDate = formatDateUSShort(new Date().toISOString());

        setSuccessMessage({
          message: "Approved successfully",
          details: approvalDate,
        });
        setIsSuccessNotificationOpen(true);

        // Clear selected item
        setSelectedApprovalItem(null);
      } else {
        // Handle error case
        setSuccessMessage({
          message: "Failed to approve request",
          details: (result.payload as string) || "An error occurred",
        });
        setIsSuccessNotificationOpen(true);
      }
    } catch (error) {
      // Handle unexpected errors
      setSuccessMessage({
        message: "Failed to approve request",
        details: "An unexpected error occurred",
      });
      setIsSuccessNotificationOpen(true);
    }
  };

  const handleRejectClick = (item: UploadItem) => {
    setSelectedRejectionItem(item);
    setIsRejectionModalOpen(true);
  };

  const handleRejectionConfirm = async (reason: string) => {
    if (!selectedRejectionItem) return;

    try {
      // Dispatch API call to reject the request
      const result = await dispatch(
        approveOrRejectRequest({
          batch_id: selectedRejectionItem.id,
          status: "rejected",
        })
      );

      // Check if the action was fulfilled
      if (approveOrRejectRequest.fulfilled.match(result)) {
        // Close the rejection modal
        setIsRejectionModalOpen(false);

        // Show success notification
        const rejectionDate = formatDateUSShort(new Date().toISOString());

        setSuccessMessage({
          message: "Rejected successfully",
          details: rejectionDate,
        });
        setIsSuccessNotificationOpen(true);

        // Clear selected item
        setSelectedRejectionItem(null);
      } else {
        // Handle error case
        setSuccessMessage({
          message: "Failed to reject request",
          details: (result.payload as string) || "An error occurred",
        });
        setIsSuccessNotificationOpen(true);
      }
    } catch (error) {
      // Handle unexpected errors
      setSuccessMessage({
        message: "Failed to reject request",
        details: "An unexpected error occurred",
      });
      setIsSuccessNotificationOpen(true);
    }
  };

  const filteredData = uploadData.filter(item => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.uploader.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredData.length / parseInt(itemsPerPage));
  const startIndex = (currentPage - 1) * parseInt(itemsPerPage);
  const endIndex = startIndex + parseInt(itemsPerPage);
  const currentData = filteredData.slice(startIndex, endIndex);

  const exOpsTabs = [
    {
      id: "all",
      label: "All",
      icon: <Grid3x3 size={18} />,
    },
    {
      id: "sponsors",
      label: "Sponsors",
      icon: <Users size={18} />,
    },
    {
      id: "accounts",
      label: "Accounts",
      icon: <Database size={18} />,
    },
    {
      id: "composites",
      label: "Composites",
      icon: <Layers size={18} />,
    },
  ];

  const teamTabs = [
    {
      id: "all",
      label: "All",
      icon: <Grid3x3 size={18} />,
    },
    {
      id: "account-team",
      label: "Account Team",
      icon: <Users size={18} />,
    },
    {
      id: "customer-team",
      label: "Customer Team",
      icon: <UserRound size={18} />,
    },
  ];

  const activeSubTabs =
    activeUploadCard === "teams-upload" ? teamTabs : exOpsTabs;

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const [metricsRunActive, setMetricsRunActive] = useState(false);
  const [metricsWorkflowOpen, setMetricsWorkflowOpen] = useState(false);
  const [leaveMetricsOpen, setLeaveMetricsOpen] = useState(false);
  const [pendingUploadCard, setPendingUploadCard] = useState<string | null>(
    null
  );

  const applyUploadTypeChange = (viewId: string) => {
    setActiveUploadCard(viewId);
    if (viewId === "ex-ops-upload" || viewId === "teams-upload") {
      setActiveTab("all");
      setCurrentPage(1);
    }
  };

  const handleUploadTypeChange = (viewId: string) => {
    if (
      activeUploadCard === "metrics-data-upload" &&
      metricsRunActive &&
      viewId !== "metrics-data-upload"
    ) {
      setPendingUploadCard(viewId);
      setLeaveMetricsOpen(true);
      return;
    }
    applyUploadTypeChange(viewId);
  };

  const confirmLeaveMetricsRun = () => {
    const next = pendingUploadCard;
    setLeaveMetricsOpen(false);
    setPendingUploadCard(null);
    setMetricsRunActive(false);
    setMetricsWorkflowOpen(false);
    if (next) applyUploadTypeChange(next);
  };

  const isMetricsUpload = activeUploadCard === "metrics-data-upload";

  const metricsWorkflowFill = isMetricsUpload && metricsWorkflowOpen;

  return (
    <div className="min-h-0 p-0">
      <CommonModal
        isOpen={leaveMetricsOpen}
        toggle={() => {
          setLeaveMetricsOpen(false);
          setPendingUploadCard(null);
        }}
        title="Leave this run?"
        size="sm"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              onClick={() => {
                setLeaveMetricsOpen(false);
                setPendingUploadCard(null);
              }}
            >
              Stay
            </Button>
            <Button onClick={confirmLeaveMetricsRun}>Leave run</Button>
          </div>
        }
      >
        <p className="m-0 text-sm text-gray-600">
          Your progress is saved. You can resume this run later from Pipeline
          Runs.
        </p>
      </CommonModal>

      {/* Shared Data Hub shell — same white card for Ex Ops, Team, and Metrics.
          Workflow steps grow with content; the page scrolls (no nested pane). */}
      <div
        className={
          metricsWorkflowFill
            ? "mb-0 rounded-[15px] border border-gray-300 bg-white p-6 pb-0"
            : "mb-6 rounded-[15px] border border-gray-300 bg-white p-6"
        }
      >
        {/* Row 1: upload type tabs (left) | sub-tabs + CTA for Ex Ops / Team (right) */}
        <div className="mb-6 flex shrink-0 flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <TabsComponent
            tabs={uploadTypeViews}
            activeTab={activeUploadCard}
            onTabChange={handleUploadTypeChange}
            variant="secondary-segmented"
            segmentedLayout="fit"
            className="rounded-full"
          />
          {!isMetricsUpload ? (
            <div className="flex flex-wrap items-center gap-4">
              <TabsComponent
                tabs={activeSubTabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                variant="tab-underline"
                className="mb-0"
              />
              <span className="hidden h-5 w-px shrink-0 bg-gray-200 sm:block" />
              <Button
                variant="primary"
                className="flex shrink-0 items-center gap-2 rounded-lg px-[18px] py-2.5 text-sm font-medium"
                onClick={() => setIsUploadModalOpen(true)}
              >
                <Upload className="h-5 w-5" />
                Upload Data
              </Button>
            </div>
          ) : (
            <div
              ref={setMetricsHeaderActionsEl}
              className="ml-auto flex w-full flex-wrap items-center justify-end gap-2 xl:w-auto"
            />
          )}
        </div>

        {(activeUploadCard === "ex-ops-upload" ||
          activeUploadCard === "teams-upload") && (
          <>
            {/* Title + Search/Filter, then Pagination */}
            <div className="mb-5 rounded-lg border border-gray-300 bg-white p-5">
              <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="mb-0 text-lg font-semibold text-gray-900">
                    Uploads & Approvals
                  </p>
                  <p className="mb-0 mt-1 text-sm text-gray-600">
                    {activeUploadCard === "teams-upload"
                      ? "Track and manage your team data approvals"
                      : "Track and manage your data approvals"}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-3">
                  <div className="relative w-56 max-w-full">
                    <Search
                      className="absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-gray-400"
                      size={16}
                    />
                    <input
                      type="text"
                      placeholder="Search by batch name..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-[42px] pr-3.5 text-sm transition-all duration-200 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:outline-none"
                    />
                  </div>
                  <div className="flex h-[38px] w-[38px] flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-gray-50 transition-all duration-200 hover:border-gray-300 hover:bg-white">
                    <Filter size={16} className="h-4 w-4 text-gray-500" />
                  </div>
                  <DropdownSelector
                    label="All Status"
                    selected={statusFilter}
                    options={[
                      { label: "All Status", value: "all" },
                      { label: "Pending", value: "pending" },
                      { label: "Approved", value: "approved" },
                      { label: "Rejected", value: "rejected" },
                      { label: "Activated", value: "activated" },
                    ]}
                    onSelect={value => {
                      setStatusFilter(value);
                      setCurrentPage(1);
                    }}
                    variant="segmented"
                  />
                </div>
              </div>

              {/* Pagination Row */}
              <div className="mt-4 flex w-full items-center justify-between gap-3 border-t border-gray-200 pt-4">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="whitespace-nowrap text-sm font-normal text-gray-700">
                    Showing {filteredData.length === 0 ? 0 : startIndex + 1} to{" "}
                    {Math.min(endIndex, filteredData.length)} of{" "}
                    {filteredData.length} uploads
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={e => {
                      setItemsPerPage(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="cursor-pointer appearance-none rounded-md border border-gray-200 bg-white bg-[length:12px_12px] bg-[position:right_8px_center] bg-no-repeat px-3 py-1.5 pr-8 text-[13px] font-normal text-gray-700 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 focus:border-purple-500 focus:outline-none focus:ring-3 focus:ring-purple-100 [background-image:url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C/polyline%3E%3C/svg%3E')]"
                  >
                    <option value="5">5 per page</option>
                    <option value="10">10 per page</option>
                    <option value="20">20 per page</option>
                    <option value="50">50 per page</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="cursor-pointer rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:opacity-50"
                  >
                    Previous
                  </Button>
                  <span className="whitespace-nowrap px-3 text-sm text-gray-500">
                    Page {totalPages === 0 ? 0 : currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="cursor-pointer rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:opacity-50"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>

            {/* Upload List */}
            <div>
              {approvalRequestsLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading...</p>
                </div>
              ) : currentData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Inbox className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-base font-medium text-gray-600">
                    No uploads found
                  </p>
                </div>
              ) : (
                currentData.map(item => {
                  const isExpanded = expandedCards.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className={`rounded-lg mb-2 border border-gray-200 bg-white transition-all duration-200 cursor-pointer relative overflow-hidden border-l-4 ${getStatusBorderColor(item.status)} ${
                        isExpanded
                          ? "shadow-lg -translate-y-0.5 border-gray-400"
                          : "hover:shadow-md hover:-translate-y-px hover:border-gray-300"
                      }`}
                      onClick={() => toggleCardExpansion(item.id)}
                    >
                      <div className="flex justify-between items-center px-5 py-4">
                        <div className="flex items-center gap-4 flex-1">
                          <div
                            className={`text-gray-400 transition-all duration-300 flex items-center justify-center w-6 h-6 rounded bg-gray-100 ${
                              isExpanded ? "rotate-90" : "rotate-0"
                            } hover:text-gray-600 hover:bg-gray-200`}
                          >
                            <ChevronRight size={16} className="flex-shrink-0" />
                          </div>
                          <div className="text-gray-500 flex items-center justify-center w-8 h-8 bg-gray-100 rounded-md">
                            <Users size={20} className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="text-base font-medium text-gray-800 mb-1">
                              {formatUploadName(
                                item.name,
                                item.uploadType || ""
                              )}
                            </div>
                            <div className="text-sm text-gray-500 mb-1">
                              {item.records} Records • {item.uploader}
                            </div>
                            <div className="text-sm text-gray-500">
                              Status:{" "}
                              <span
                                className={`font-medium ${getStatusTextColor(item.status)}`}
                              >
                                {item.uploadType === "delete_customer" &&
                                item.status === "approved"
                                  ? "Account Deleted"
                                  : item.status.charAt(0).toUpperCase() +
                                    item.status.slice(1)}
                              </span>
                              {item.approverName && (
                                <>
                                  <span className="mx-1.5 text-gray-400">
                                    •
                                  </span>
                                  <span className="text-gray-500">
                                    Approver:{" "}
                                    <span className="font-medium text-gray-700">
                                      {item.approverName}
                                    </span>
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div
                            className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize tracking-wide border-none ${getStatusBadgeColor(item.status)}`}
                          >
                            {item.uploadType === "delete_customer" &&
                            item.status === "approved"
                              ? "Account Deleted"
                              : item.status.charAt(0).toUpperCase() +
                                item.status.slice(1)}
                          </div>
                          <div className="text-[13px] text-gray-500 font-normal">
                            {item.date}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="p-4">
                          {/* Timeline */}
                          <div className="relative">
                            <div className="flex items-start gap-0 relative py-6">
                              {/* Uploaded Step */}
                              <div className="flex flex-col items-center relative flex-1 text-center z-20">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center mb-2 flex-shrink-0 bg-green-500 text-white relative z-30">
                                  <Upload size={18} className="h-4 w-4" />
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-sm font-medium text-green-500 text-center">
                                    {isBulkUploadType(item.uploadType)
                                      ? "Uploaded"
                                      : "Created"}
                                  </span>
                                  <span className="text-xs text-center font-medium">
                                    {item.uploader}
                                  </span>
                                  <span className="text-xs text-gray-500 text-center">
                                    {item.uploadDate || item.date}
                                  </span>
                                </div>
                                {/* Connecting line from Uploaded to Under Review - always green */}
                                <div className="absolute top-4 left-1/2 right-[-50%] h-0.5 bg-green-500 z-10"></div>
                              </div>

                              {/* Under Review Step */}
                              <div className="flex flex-col items-center relative flex-1 text-center z-20">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center mb-2 flex-shrink-0 bg-green-500 text-white relative z-30">
                                  <Clock size={18} className="h-4 w-4" />
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                  <span className="text-sm font-medium text-green-500 text-center">
                                    Under Review
                                  </span>
                                  <span className="text-xs text-gray-500 text-center">
                                    {item.status === "pending"
                                      ? "In Progress"
                                      : item.approvalDate ||
                                        item.rejectionDate ||
                                        item.activationDate}
                                  </span>
                                </div>
                                {/* Connecting line from Under Review to Final Status */}
                                <div
                                  className={`absolute top-4 left-1/2 right-[-50%] h-0.5 z-10 ${
                                    item.status === "approved" ||
                                    item.status === "activated"
                                      ? "bg-green-500"
                                      : item.status === "rejected"
                                        ? "bg-red-500"
                                        : item.status === "pending"
                                          ? "bg-gray-300"
                                          : "bg-gray-200"
                                  }`}
                                ></div>
                              </div>

                              {/* Final Status Step */}
                              <div className="flex flex-col items-center relative flex-1 text-center z-20">
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 flex-shrink-0 relative z-30 ${
                                    item.status === "approved" ||
                                    item.status === "activated"
                                      ? "bg-green-500 text-white"
                                      : item.status === "rejected"
                                        ? "bg-red-500 text-white"
                                        : "bg-gray-300 text-gray-400"
                                  }`}
                                >
                                  {item.status === "rejected" ? (
                                    <X size={18} className="h-4 w-4" />
                                  ) : (
                                    <CheckCircle
                                      size={18}
                                      className="h-4 w-4"
                                    />
                                  )}
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                  <span
                                    className={`text-sm font-medium text-center ${
                                      item.status === "approved" ||
                                      item.status === "activated"
                                        ? "text-green-500"
                                        : item.status === "rejected"
                                          ? "text-red-500"
                                          : "text-gray-700"
                                    }`}
                                  >
                                    {item.status === "rejected"
                                      ? "Rejected"
                                      : item.status === "approved"
                                        ? "Approved"
                                        : item.status === "activated"
                                          ? "Activated"
                                          : "Pending"}
                                  </span>
                                  {item.status === "approved" &&
                                    item.approvedBy && (
                                      <span className="text-xs text-center font-medium">
                                        {item.approvedBy}
                                      </span>
                                    )}
                                  {item.status === "rejected" &&
                                    item.rejectedBy && (
                                      <span className="text-xs text-center font-medium">
                                        {item.rejectedBy}
                                      </span>
                                    )}
                                  {item.status === "activated" &&
                                    item.activatedBy && (
                                      <span className="text-xs text-gray-500 text-center">
                                        {item.activatedBy}
                                      </span>
                                    )}
                                  <span className="text-xs text-gray-500 text-center">
                                    {item.approvalDate ||
                                      item.rejectionDate ||
                                      item.activationDate ||
                                      "Pending"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div
                            className="flex gap-3 justify-end mb-4 pt-4 border-gray-100"
                            onClick={e => e.stopPropagation()}
                          >
                            <button
                              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-700 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-gray-200 hover:border-gray-300 active:scale-95"
                              onClick={() => handlePreviewClick(item)}
                            >
                              <Eye size={18} className="w-[18px] h-[18px]" />
                              Preview
                            </button>
                            {item.status === "pending" && (
                              <>
                                <button
                                  className={`inline-flex items-center gap-2 px-4 py-2 border-none rounded-lg text-sm font-medium transition-all duration-200 ${
                                    canApproveForItem(item)
                                      ? "bg-green-500 text-white hover:bg-green-600 active:scale-95 shadow-sm hover:shadow cursor-pointer"
                                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                  }`}
                                  onClick={() =>
                                    canApproveForItem(item) &&
                                    handleApproveClick(item)
                                  }
                                  disabled={!canApproveForItem(item)}
                                >
                                  <Check
                                    size={18}
                                    className="w-[18px] h-[18px]"
                                  />
                                  Approve
                                </button>
                                <button
                                  className={`inline-flex items-center gap-2 px-4 py-2 border-none rounded-lg text-sm font-medium transition-all duration-200 ${
                                    canApproveForItem(item)
                                      ? "bg-red-600 text-white hover:bg-red-700 active:scale-95 shadow-sm hover:shadow cursor-pointer"
                                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                  }`}
                                  onClick={() =>
                                    canApproveForItem(item) &&
                                    handleRejectClick(item)
                                  }
                                  disabled={!canApproveForItem(item)}
                                >
                                  <X size={18} className="w-[18px] h-[18px]" />
                                  Reject
                                </button>
                              </>
                            )}
                          </div>

                          {/* Success/Error Messages */}
                          {item.status === "activated" && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-4 flex items-start gap-3">
                              <Bell
                                size={20}
                                className="h-5 w-5 text-green-500 flex-shrink-0"
                              />
                              <div className="flex-1">
                                <h4 className="text-base font-semibold text-green-800 m-0 mb-2">
                                  Upload Activated Successfully!
                                </h4>
                                <p className="text-sm text-green-800 m-0 mb-1 leading-relaxed">
                                  {item.records} records have been added to the
                                  system and affected executives have been
                                  notified.
                                </p>
                                <p className="text-sm text-green-800 m-0 leading-relaxed">
                                  Activated by {item.activatedBy} on{" "}
                                  {item.activationDate}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {isMetricsUpload && (
          <div>
            <DataHubPage
              orgName={metricsOrgName}
              title="Metrics Upload"
              embedded
              headerActionsContainer={metricsHeaderActionsEl}
              onRunActiveChange={setMetricsRunActive}
              onWorkflowOpenChange={setMetricsWorkflowOpen}
            />
          </div>
        )}
      </div>

      {/* Data Upload Manager Modal */}
      {isUploadModalOpen && (
        <DataUploadManager
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          mode={activeUploadCard === "teams-upload" ? "team" : "ex-ops"}
          initialActiveType={
            activeUploadCard === "teams-upload"
              ? (TEAM_TAB_UPLOAD_MAP[activeTab]?.modalType ??
                "upload_account_team_members")
              : (EX_OPS_TAB_UPLOAD_MAP[activeTab]?.modalType ?? "executives")
          }
          approvalUploadType={getActiveApiType()}
        />
      )}

      {/* Preview Modal */}
      {selectedPreviewItem && (
        <PreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          title={formatUploadName(
            selectedPreviewItem.name,
            selectedPreviewItem.uploadType || ""
          )}
          recordCount={`${selectedPreviewItem.records} ${
            selectedPreviewItem.type === "team"
              ? "team member"
              : selectedPreviewItem.type
          } records`}
          uploadType={
            selectedPreviewItem.uploadType ?? selectedPreviewItem.type
          }
          data={selectedPreviewItem.previewData || []}
        />
      )}

      {/* Approval Modal */}
      {selectedApprovalItem && (
        <ApprovalModal
          isOpen={isApprovalModalOpen}
          onClose={() => setIsApprovalModalOpen(false)}
          onConfirm={handleApprovalConfirm}
          uploadName={formatUploadName(
            selectedApprovalItem.name,
            selectedApprovalItem.uploadType || ""
          )}
        />
      )}

      {/* Success Notification */}
      <SuccessNotification
        isOpen={isSuccessNotificationOpen}
        onClose={() => setIsSuccessNotificationOpen(false)}
        message={successMessage.message}
        details={successMessage.details}
      />

      <RejectionModal
        isOpen={isRejectionModalOpen}
        onClose={() => setIsRejectionModalOpen(false)}
        onConfirm={handleRejectionConfirm}
        uploadName={
          selectedRejectionItem
            ? formatUploadName(
                selectedRejectionItem.name,
                selectedRejectionItem.uploadType || ""
              )
            : ""
        }
      />
    </div>
  );
};

export default OverviewContainer;
